import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { CommonService } from '../../common-service';
import { ReturnStruct } from '../../structs/return.struct';
import { OrdemProducaoListaRequest } from '../../requests/suprimentos/ordem-producao-lista.request';
import { SalvarOrdemProducaoRequest } from '../../requests/suprimentos/salvar-ordem-producao.request';
import { GetOrdemProducaoResponse } from '../../responses/suprimentos/get-ordem-producao.response';
import { GetOrdemProducaoByIdResponse } from '../../responses/suprimentos/get-ordem-producao-by-id.response';
import { CriarOrdemProducaoResponse } from '../../responses/suprimentos/criar-ordem-producao.response';
import { ComposicaoOrdemProducaoResponse } from '../../responses/suprimentos/composicao-ordem-producao.response';
import { GerarOrdensRelacionadasResponse } from '../../responses/suprimentos/gerar-ordens-relacionadas.response';
import { GetOrdemProducaoHistoricoResponse } from '../../responses/suprimentos/get-ordem-producao-historico.response';
import { CriarOrdemProducaoOcorrenciaRequest } from '../../requests/suprimentos/criar-ordem-producao-ocorrencia.request';
import { OrdemProducaoEstoqueExcluirRequest } from '../../requests/suprimentos/ordem-producao-estoque-excluir.request';
import { OrdemProducaoIntegracaoResponse } from '../../responses/suprimentos/ordem-producao-integracao.response';
import { LancarEstoqueOrdemProducaoRequest } from '../../requests/suprimentos/lancar-estoque-ordem-producao.request';
import { GetResumoEstoqueOrdemProducaoResponse } from '../../responses/suprimentos/get-resumo-estoque-ordem-producao.response';
import { GetEstruturaProdutoOrdemProducaoResponse } from '../../responses/suprimentos/get-estrutura-produto-ordem-producao.response';

/**
 * Chamadas que precisam juntar API.Suprimentos + API.Produto + API.Usuario. A lista e o detalhe já
 * voltam com nome/SKU/unidade do produto e nome do usuário resolvidos.
 */
@Injectable({ providedIn: 'root' })
export class OrdemProducaoOrquestradorService extends CommonService {
  private readonly apiUrl: string = environment.urlApiOrquestradorSuprimentos + 'OrdemProducaoOrquestrador';

  constructor(private httpClient: HttpClient) {
    super();
  }

  /**
   * Lista paginada. `paginaAtual` é 0-based e vai em query; os filtros vão no corpo, porque são
   * muitos e incluem listas.
   */
  public getAll(request: OrdemProducaoListaRequest, paginaAtual: number = 0): Observable<GetOrdemProducaoResponse> {
    const params = new HttpParams().set('paginaAtual', paginaAtual.toString());

    return this.httpClient
      .post<GetOrdemProducaoResponse>(`${this.apiUrl}/getAll`, request, { params, ...this.addHeaderToken() })
      .pipe(catchError(this.handleError));
  }

  public getById(idOrdemProducao: number): Observable<GetOrdemProducaoByIdResponse> {
    return this.httpClient
      .get<GetOrdemProducaoByIdResponse>(`${this.apiUrl}/idOrdemProducao/${idOrdemProducao}`, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /**
   * Preview da composição e das etapas. Chamado quando produto ou quantidade mudam no cadastro —
   * os valores exibidos não são reenviados na gravação, que recalcula tudo no servidor.
   */
  public getComposicao(idProduto: number, quantidade: number): Observable<ComposicaoOrdemProducaoResponse> {
    const params = new HttpParams()
      .set('idProduto', idProduto.toString())
      .set('quantidade', quantidade.toString());

    return this.httpClient
      .get<ComposicaoOrdemProducaoResponse>(`${this.apiUrl}/composicao`, { params, ...this.addHeaderToken() })
      .pipe(catchError(this.handleError));
  }

  public criar(request: SalvarOrdemProducaoRequest): Observable<CriarOrdemProducaoResponse> {
    return this.httpClient
      .post<CriarOrdemProducaoResponse>(this.apiUrl, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  public atualizar(idOrdemProducao: number, request: SalvarOrdemProducaoRequest): Observable<ReturnStruct> {
    return this.httpClient
      .put<ReturnStruct>(`${this.apiUrl}/idOrdemProducao/${idOrdemProducao}`, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /**
   * Explode a hierarquia: cria uma OP para cada insumo da composição que seja, ele próprio, um
   * produto Fabricado, recursivamente. Devolve a lista do que criou — vazia quando não há insumo
   * fabricado, e o diálogo da tela aparece de qualquer forma.
   */
  public gerarOrdensRelacionadas(idOrdemProducao: number): Observable<GerarOrdensRelacionadasResponse> {
    return this.httpClient
      .post<GerarOrdensRelacionadasResponse>(
        `${this.apiUrl}/idOrdemProducao/${idOrdemProducao}/gerar-ordens-relacionadas`, {}, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /** Ocorrências da OP (histórico), por data do fato desc, com o nome do usuário já resolvido nas linhas antigas. */
  public getHistorico(idOrdemProducao: number): Observable<GetOrdemProducaoHistoricoResponse> {
    return this.httpClient
      .get<GetOrdemProducaoHistoricoResponse>(`${this.apiUrl}/idOrdemProducao/${idOrdemProducao}/historico`, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /** Grava a anotação do usuário no histórico da OP (tipo Anotação, origem Usuário), em qualquer status. */
  public criarOcorrencia(idOrdemProducao: number, request: CriarOrdemProducaoOcorrenciaRequest): Observable<ReturnStruct> {
    return this.httpClient
      .post<ReturnStruct>(`${this.apiUrl}/idOrdemProducao/${idOrdemProducao}/ocorrencia`, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /**
   * Lançamento de estoque de 1+ OPs — o diálogo individual do Detalhe manda uma lista de 1 item,
   * o diálogo de consolidação da Lista (lote) manda N. Mesmo endpoint pros dois.
   */
  public lancarEstoque(request: LancarEstoqueOrdemProducaoRequest): Observable<OrdemProducaoIntegracaoResponse> {
    return this.httpClient
      .post<OrdemProducaoIntegracaoResponse>(`${this.apiUrl}/estoque/lancar`, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /** Exclui o estoque da OP (apaga as movimentações dela no Produto); a justificativa é obrigatória. */
  public excluirEstoque(
    idOrdemProducao: number,
    request: OrdemProducaoEstoqueExcluirRequest,
  ): Observable<OrdemProducaoIntegracaoResponse> {
    return this.httpClient
      .post<OrdemProducaoIntegracaoResponse>(
        `${this.apiUrl}/idOrdemProducao/${idOrdemProducao}/estoque/excluir`, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /** Alimenta o cartão de resumo do lançamento (8.3-bis) e o diálogo de exclusão do estoque. */
  public getResumoEstoque(idOrdemProducao: number): Observable<GetResumoEstoqueOrdemProducaoResponse> {
    return this.httpClient
      .get<GetResumoEstoqueOrdemProducaoResponse>(`${this.apiUrl}/idOrdemProducao/${idOrdemProducao}/estoque/resumo`, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /**
   * Explosão recursiva multinível da estrutura (BOM) do produto da OP, já achatada em pré-ordem —
   * impressão "Estrutura do Produto" (seção 7.2).
   */
  public getEstrutura(idOrdemProducao: number): Observable<GetEstruturaProdutoOrdemProducaoResponse> {
    return this.httpClient
      .get<GetEstruturaProdutoOrdemProducaoResponse>(`${this.apiUrl}/idOrdemProducao/${idOrdemProducao}/estrutura`, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }
}
